import { CSSProperties, useEffect, useRef, useState } from 'react';
import { TImage, TMediaFit, TImageWithMetadata } from '@components/typescript';
import { DEFAULT_THUMBNAIL_SIZES } from '@features/categories/const';
import { IThumbnailFormats } from '@features/categories/typescript';
import useImageSources from '@hooks/useImageSources';
import encodeSrcSetUrl from '@utils/encodeSrcSetUrl';
import { getPictureMediaMinWidthsPx } from '@utils/getPictureMediaMinWidths';
import GetImageAlt from '@utils/getImageAlt';
import { buildThumbnailSrcSet, getThumbnailImgSrc, hasThumbnailFormats, resolveThumbnailFormats } from '@utils/thumbnailImageSources';
const TRUSTED_HOSTNAMES = ['vox.pl', 'googleapis.com', 'gstatic.com', 'storage.cloud.google.com'];

/** Uploady creative trzymają tylko oryginalny raster — nie ma pochodnych .avif/.webp. */
const RASTER_ONLY_PATH_MARKERS = ['creative-configurator-photo'];
const isTrustedDomain = (url: string): boolean => {
  if (!url.startsWith('https://')) return false;
  try {
    const {
      hostname
    } = new URL(url);
    return TRUSTED_HOSTNAMES.some(h => hostname === h || hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
};
const canDeriveModernFormats = (url: string): boolean => {
  if (!isTrustedDomain(url)) return false;
  return !RASTER_ONLY_PATH_MARKERS.some(marker => url.includes(marker));
};
const toWebp = (url: string): string | null => {
  if (!canDeriveModernFormats(url)) return null;
  const match = url.match(/^(.+)\.(jpe?g|png)$/i);
  return match ? `${match[1]}.webp` : null;
};
const toAvif = (url: string): string | null => {
  if (!canDeriveModernFormats(url)) return null;
  const match = url.match(/^(.+)\.(jpe?g|png)$/i);
  return match ? `${match[1]}.avif` : null;
};

/** Czy dla URL da się wygenerować pochodne AVIF/WebP (tylko JPG/PNG na zaufanych hostach). */
const pathHasDerivedModernFormats = (url: string): boolean => Boolean(toAvif(url) || toWebp(url));
const thumbnailHasModernFormats = (formats: IThumbnailFormats): boolean => Boolean(buildThumbnailSrcSet(formats.avif) || buildThumbnailSrcSet(formats.webp));
type PropsType = {
  image: TImage | TImageWithMetadata | string;
  /** Gotowe formaty miniatury (webp/avif/jpg z 1x/2x). */
  thumbnailFormats?: IThumbnailFormats;
  /** Mapa rozmiarów miniatury — rozwiązywana przez `thumbnailSizes`. */
  thumbnails?: Record<string, IThumbnailFormats | undefined>;
  /** Kolejność rozmiarów do wyboru z `thumbnails`; domyślnie `DEFAULT_THUMBNAIL_SIZES`. */
  thumbnailSizes?: string[];
  alt?: string;
  blur?: TImage | string;
  objectFit?: TMediaFit;
  objectPosition?: string;
  style?: CSSProperties;
  className?: string;
  sx?: Record<string, unknown>;
  aspectRatio?: string;
  dataCy?: string;
  priority?: boolean;
  width?: number;
  height?: number;
  onLoad?: () => void;
  onError?: () => void;
};
const isImageWithMetadata = (img: unknown): img is TImageWithMetadata => typeof img === 'object' && img !== null && 'path' in img;
const renderThumbnailSources = (formats: IThumbnailFormats) => {
  const avif = buildThumbnailSrcSet(formats.avif);
  const webp = buildThumbnailSrcSet(formats.webp);
  const jpg = buildThumbnailSrcSet(formats.jpg);
  return [avif ? <source key="thumb-avif" srcSet={avif} type="image/avif" /> : null, webp ? <source key="thumb-webp" srcSet={webp} type="image/webp" /> : null, jpg ? <source key="thumb-jpg" srcSet={jpg} type="image/jpeg" /> : null];
};
const ResponsiveImage = ({
  image,
  thumbnailFormats,
  thumbnails,
  thumbnailSizes = DEFAULT_THUMBNAIL_SIZES,
  alt = '',
  blur,
  objectFit = 'cover',
  objectPosition = 'center',
  style,
  className,
  sx,
  aspectRatio,
  dataCy,
  priority = false,
  width,
  height,
  onLoad,
  onError
}: PropsType) => {
  const imgRef = useRef<HTMLImageElement>(null);
  /** Po 404 AVIF/WebP pomijamy `<source>` i ładujemy raster z `src` (picture nie robi takiego fallbacku). */
  const [useRasterOnly, setUseRasterOnly] = useState(false);
  const hasDimensions = width !== undefined && height !== undefined;
  const normalizedImage: TImage = typeof image === 'string' ? {
    desktop: image,
    tablet: image,
    mobile: image
  } : isImageWithMetadata(image) ? {
    desktop: image,
    tablet: image,
    mobile: image
  } : image;
  const normalizedBlur = typeof blur === 'string' ? undefined : blur;
  const blurFallback = typeof blur === 'string' ? blur : '';
  const legacyFallbackSrc = typeof image === 'string' ? image : isImageWithMetadata(image) ? image.path : '';
  const resolvedThumbnailFormats = (hasThumbnailFormats(thumbnailFormats) ? thumbnailFormats : undefined) || resolveThumbnailFormats(thumbnails, thumbnailSizes);
  const {
    getCoarseVariantPaths
  } = useImageSources({
    image: normalizedImage,
    blur: normalizedBlur
  });
  const {
    mobile: mobilePath,
    tablet: tabletPath,
    desktop: desktopPath
  } = getCoarseVariantPaths();
  const legacyMainSrc = mobilePath || tabletPath || desktopPath || blurFallback || legacyFallbackSrc;
  const usesThumbnailFormats = hasThumbnailFormats(resolvedThumbnailFormats);
  const mainSrc = usesThumbnailFormats ? getThumbnailImgSrc(resolvedThumbnailFormats!, legacyMainSrc) : legacyMainSrc;

  /**
   * useRasterOnly usuwa wszystkie `<source>` (w tym media desktop/tablet).
   * Ma sens tylko gdy faktycznie emitowaliśmy pochodne AVIF/WebP — np. nie dla
   * vox-files.com / już .webp, gdzie nie ma takich pochodnych z JPG.
   */
  const hasModernFormatFallback = usesThumbnailFormats ? thumbnailHasModernFormats(resolvedThumbnailFormats!) : [mainSrc, desktopPath, tabletPath].filter(Boolean).some(path => pathHasDerivedModernFormats(path!));
  useEffect(() => {
    setUseRasterOnly(false);
  }, [mainSrc]);
  useEffect(() => {
    const img = imgRef.current;
    if (!img?.complete) return;
    if (img.naturalHeight > 0) {
      onLoad?.();
      return;
    }
    // `loading=lazy` often reports complete + 0 size before fetch; currentSrc may already
    // be set from <picture> source selection — do not treat as AVIF/WebP failure.
    if (img.loading === 'lazy') return;
    if (!hasModernFormatFallback) {
      onError?.();
      return;
    }
    if (useRasterOnly) {
      onError?.();
    } else {
      setUseRasterOnly(true);
    }
  }, [mainSrc, useRasterOnly, onLoad, onError, hasModernFormatFallback]);
  if (!mainSrc) return null;
  const isUsingBlur = !usesThumbnailFormats && !mobilePath && !tabletPath && !desktopPath;
  const {
    tablet: tabletMin,
    desktop: desktopMin
  } = getPictureMediaMinWidthsPx();
  const imageAlt = GetImageAlt(normalizedImage) || alt;
  const hasDesktopVariant = !usesThumbnailFormats && Boolean(desktopPath && desktopPath !== mainSrc);
  const hasTabletVariant = !usesThumbnailFormats && Boolean(tabletPath && tabletPath !== mainSrc);
  const handleImageError = () => {
    if (!useRasterOnly && hasModernFormatFallback) {
      setUseRasterOnly(true);
      return;
    }
    onError?.();
  };
  const imgStyle: CSSProperties = hasDimensions ? {
    objectFit,
    objectPosition,
    ...style
  } : {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit,
    objectPosition,
    ...(isUsingBlur ? {
      filter: 'blur(20px)',
      transform: 'scale(1.1)'
    } : {}),
    ...style
  };
  const renderSources = (path: string, media?: string) => {
    const avif = toAvif(path);
    const webp = toWebp(path);
    const mediaKey = media ?? 'default';
    return [avif ? <source key={`${mediaKey}-avif`} srcSet={encodeSrcSetUrl(avif)} type="image/avif" {...media ? {
      media
    } : {}} /> : null, webp ? <source key={`${mediaKey}-webp`} srcSet={encodeSrcSetUrl(webp)} type="image/webp" {...media ? {
      media
    } : {}} /> : null, media ? <source key={`${mediaKey}-raster`} srcSet={encodeSrcSetUrl(path)} media={media} /> : null];
  };
  return <picture key={useRasterOnly ? 'raster-only' : 'with-sources'} style={hasDimensions ? undefined : {
    display: 'block',
    width: '100%',
    height: '100%',
    aspectRatio
  }}>
      {!useRasterOnly && usesThumbnailFormats && renderThumbnailSources(resolvedThumbnailFormats!)}
      {!useRasterOnly && hasDesktopVariant && renderSources(desktopPath!, `(min-width: ${desktopMin}px)`)}
      {!useRasterOnly && hasTabletVariant && renderSources(tabletPath!, `(min-width: ${tabletMin}px)`)}
      {!useRasterOnly && !usesThumbnailFormats && renderSources(mainSrc)}
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: image load/error lifecycle callbacks */}
      <img ref={imgRef} src={encodeSrcSetUrl(mainSrc)} alt={isUsingBlur ? '' : imageAlt} className={className} sx={sx} style={imgStyle} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : undefined} decoding="async" data-cy={dataCy} width={width} height={height} onLoad={onLoad} onError={handleImageError} data-testid="responsive-image" />
    </picture>;
};
export default ResponsiveImage;